import type { NextPage } from "next"
import Head from "next/head"
import Image from "next/image"
import { NavBar } from "../src/components/universal/NavBar"
import { Footer } from "../src/components/universal/Footer"
import { PageShade } from "../src/components/universal/PageShade"
import { PageHeader } from "../src/components/universal/PageHeader"
import { SectionHeader } from "../src/components/elements/SectionHeader"
import { PageMeta } from "../src/components/universal/PageMeta"

// Images
import pageShade from "/public/images/shades/privacy.png"
import aboutDes from "/public/images/about/about-cover.png"
import aboutMob from "/public/images/about/about-cover-mob.png"

const About: NextPage = () => {
  return (
    <div>
      <PageMeta
        title={"About"}
        theme={"#F3F4F7"}
        description={"We are on a mission to make saving money as easy as spending it."}
      />
      <PageShade image={pageShade} />
      <main>
        <NavBar />

        <PageHeader
          center
          subtitle='About'
          title='Redefining
          Shopping & Saving'
          caption='We are on a mission to make saving money as easy as spending it.'
          imageDes={aboutDes}
          imageMob={aboutMob}
        />

        <section>
          <div className='wrapper about-story'>
            <SectionHeader
              title='Our story'
              caption={`After Ruby's founders won a Silicon Valley hackathon with a loyalty points solution, they realized that the decades-old savings industry needed to be disrupted. The big problem? Shopping and saving was too difficult. That was in 2016, and it's only getting harder. As retailers increasingly compete for our attention, the number of different ways to save has become overwhelming - and working out where to shop and which deals to combine to save the most is even harder. That's where Ruby comes in. Our app provides a seamless shopping experience. It's one platform to browse products, combine every way to save, and track your savings over time.`}
            />
          </div>
        </section>

        <section>
          <div className='wrapper members'>
            <SectionHeader title='Meet the team' />

            <div className='items'>
              <MemberItem
                avatar='/images/about/rahul.png'
                name='Rahul Talari'
                role='Co-Founder & CEO'
                bio='Rahul leverages his expertise in Machine Learning and AI to build innovative money-saving technology into Ruby'
              />
              <MemberItem
                avatar='/images/about/dk.png'
                name='Dongkeun Lee'
                role='Lead iOS Developer'
                bio="With a background in Full Stack dev and a passion for UX, Dongkeun continually improves Ruby's seamless interface."
              />
            </div>
          </div>
        </section>

        <section>
          <div className='wrapper values'>
            <SectionHeader title='What we believe in' />

            <div className='cards'>
              <ValueCard
                emoji='💎️'
                title='Pursue excellence'
                caption='Ensure you try to be the best version of yourself everyday'
                color='#BCFDFF'
              />
              <ValueCard
                emoji='✊️'
                title='Be Bold'
                caption='No arrogance but able to standup for yourself and your views/beliefs'
                color='#FFE7A0'
              />
              <ValueCard
                emoji='🏂'
                title='Take Risks'
                caption='Take the leap with faith to execute and fail fast'
                color='#E9FA83'
              />
              <ValueCard
                emoji='❤️'
                title='Be Passionate'
                caption='Love what you do and do what you love'
                color='#FFCDCA'
              />
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </div>
  )
}

export default About

// Member Item
type memberItemProps = {
  avatar?: string
  name: string
  role?: string
  bio?: string
}

export const MemberItem: React.FC<memberItemProps> = (props) => {
  return (
    <div className='member-item'>
      <img src={props.avatar} alt='Avatar' />
      <h5>{props?.name}</h5>
      <span className='body weight-500 accent'>{props?.role}</span>
      <p className='caption gray-text'>{props?.bio}</p>
    </div>
  )
}

// Value Card
type valueCardProps = {
  emoji: string
  title: string
  caption?: string
  color?: string
}

export const ValueCard: React.FC<valueCardProps> = (props) => {
  return (
    <div
      className='value-card'
      style={{
        background: props.color,
      }}
    >
      <h2>{props.emoji}</h2>
      <h4>{props?.title}</h4>
      <p className='weight-500 gray-text'>{props?.caption}</p>
    </div>
  )
}
